/**
 * Module that renders the ProcessingJSDocumentation
 * from scratchpad-documentation-package
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";

import {moduleLayout} from "#modules-package/styles.ts";
import type {UserAuthoredContentType} from "#scratchpads-package/user-authored-content-types.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";

type ProcessingJSDocumentation =
    typeof import("../../scratchpad-documentation-package/pjs-documentation.tsx").default;
type WebpageDocumentation =
    typeof import("../../scratchpad-documentation-package/webpage-documentation.tsx").default;
type SQLDocumentation =
    typeof import("../../scratchpad-documentation-package/sql-documentation.tsx").default;

type Props = {
    // Should the title be hidden? (defaults to true)
    hideTitle: boolean;
    // Is the documentation rendered within a course?
    inCourse?: boolean;
    // Should we use module-style layout (defaults to true)
    moduleLayout: boolean;
    // What type are we rendering? Processing.js? Webpages? SQL?
    type: UserAuthoredContentType;
};

type State = {
    ProcessingJSDocumentation: ProcessingJSDocumentation | null | undefined;
    WebpageDocumentation: WebpageDocumentation | null | undefined;
    SQLDocumentation: SQLDocumentation | null | undefined;
};

class ProgrammingDocumentation extends React.Component<Props, State> {
    static defaultProps: {
        hideTitle: boolean;
        moduleLayout: boolean;
    } = {
        hideTitle: true,
        moduleLayout: true,
    };

    state: State = {
        ProcessingJSDocumentation: null,
        WebpageDocumentation: null,
        SQLDocumentation: null,
    };

    componentDidMount() {
        Promise.all([
            import(
                "../../scratchpad-documentation-package/pjs-documentation.tsx"
            ).then((module) => module.default),
            import(
                "../../scratchpad-documentation-package/sql-documentation.tsx"
            ).then((module) => module.default),
            import(
                "../../scratchpad-documentation-package/webpage-documentation.tsx"
            ).then((module) => module.default),
        ]).then(
            ([
                ProcessingJSDocumentation,
                SQLDocumentation,
                WebpageDocumentation,
            ]: [any, any, any]) => {
                this.setState({
                    ProcessingJSDocumentation,
                    SQLDocumentation,
                    WebpageDocumentation,
                });
            },
        );
    }

    renderDocs: () => React.ReactNode = () => {
        const Documentation = {
            pjs: this.state.ProcessingJSDocumentation,
            webpage: this.state.WebpageDocumentation,
            sql: this.state.SQLDocumentation,
            document: null,
            python: null,
        }[this.props.type];

        return Documentation ? (
            <Documentation
                hideTitle={this.props.hideTitle}
                inCourse={this.props.inCourse}
            />
        ) : (
            <CircularSpinner />
        );
    };

    render(): React.ReactNode {
        return (
            <div
                className={css(
                    this.props.moduleLayout && styles.moduleLayout,
                    styles.module,
                )}
            >
                {this.renderDocs()}
            </div>
        );
    }
}

const styles = StyleSheet.create({
    moduleLayout: {
        ...moduleLayout.defaultAlignment,
        ...moduleLayout.defaultSpacing,
    },
    module: {
        ...deprecatedTypography.bodySmall,
    },
});

export default ProgrammingDocumentation;
